import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Space,
  Tag,
  Typography,
  Layout,
  Calendar,
  Row,
  Col,
  Badge,
  Tooltip,
  Popover
} from "antd";
import {
  FaPause,
  FaCheck,
  FaTimes,
  FaSync,
  FaClock,
  FaUser,
  FaCalendarDay,
  FaUtensils,
  FaComments,
  FaEllipsisH
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/Api";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

dayjs.locale('pt-br');

const { Title, Text } = Typography;
const { Content } = Layout;

// Configurações de cores por tipo de pausa
const PAUSA_CONFIGS = {
  'Almoço': { color: '#1890ff', icon: <FaUtensils /> },
  'Conversar com o Supervisor': { color: '#52c41a', icon: <FaComments /> },
  'Outros': { color: '#faad14', icon: <FaEllipsisH /> },
  'Supervisor': { color: '#52c41a', icon: <FaComments /> }
};

const GestaoPausas = () => {

  const [pausas, setPausas] = useState([]);
  const [mapaPausas, setMapaPausas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(dayjs());

  useEffect(() => {
    document.title = "GESTÃO DE PAUSAS";
    fetchPausas();
    fetchMapaPausas();
  }, []);

  const sms_success = (m) => toast.success(m);
  const sms_error = (m) => toast.error(m);

  // ------------------------------
  const formatDateTime = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // ------------------------------
  const fetchPausas = async () => {
    try {
      setLoading(true);
      const resp = await api.ListAllPausa();
      if (Array.isArray(resp)) setPausas(resp);
    } catch {
      sms_error("Erro ao carregar pausas");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  const fetchMapaPausas = async () => {
    try {
      const resp = await api.ListMapaPausa();
      if (Array.isArray(resp)) {
        // Ordenar por data de aprovação (mais antigas primeiro)
        const sorted = resp.sort((a, b) => 
          new Date(a.aprovacao) - new Date(b.aprovacao)
        );
        setMapaPausas(sorted);
      }
    } catch {
      sms_error("Erro ao carregar mapa de pausas");
    }
  };

  // ------------------------------
  const handleAprovar = async (pausa) => {
    try {
      const payload = {
        id: pausa.id,
        status: 1,
        aprovacao: formatDateTime(new Date())
      };

      const resp = await api.AlterPausa(payload);

      if (resp?.success) {
        sms_success("Pausa aprovada!");
        fetchPausas();
        fetchMapaPausas();
      }
    } catch {
      sms_error("Erro ao aprovar pausa");
    }
  };

  // ------------------------------
  const handleRejeitar = async (pausa) => {
    try {
      const payload = {
        id: pausa.id,
        status: 2
      };

      const resp = await api.AlterPausa(payload);

      if (resp?.success) {
        sms_success("Pausa rejeitada!");
        fetchPausas();
        fetchMapaPausas();
      }
    } catch {
      sms_error("Erro ao rejeitar pausa");
    }
  };

  // ------------------------------
  const countPendentes = pausas.filter(p => p.status === 0 || p.status === "0").length;

  // ------------------------------
  const columns = [
    { 
      title: "Operador", 
      dataIndex: "nome",
      render: (nome) => (
        <Space>
          <FaUser style={{ color: '#1890ff' }} />
          <Text>{nome}</Text>
        </Space>
      )
    },
    {
      title: "Motivo",
      dataIndex: "id_motivo",
      render: (id) => (id === 2 ? "Supervisor" : "Outros")
    },
    {
      title: "Observação",
      dataIndex: "obs",
      render: (t) => t || "-"
    },
    {
      title: "Solicitado em",
      dataIndex: "created_at",
      render: (t) => t ? dayjs(t).format('DD/MM HH:mm') : "-"
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        const map = {
          0: { color: "orange", txt: "Pendente" },
          1: { color: "green", txt: "Aprovado" },
          2: { color: "red", txt: "Rejeitado" },
          3: { color: "blue", txt: "Retornado" }
        };
        const status = typeof s === 'string' ? parseInt(s) : s;
        return <Tag color={map[status]?.color || 'default'}>{map[status]?.txt || 'Desconhecido'}</Tag>;
      }
    },
    {
      title: "Ações",
      render: (_, record) => {
        const status = typeof record.status === 'string' ? parseInt(record.status) : record.status;
        
        if (status === 0) {
          return (
            <Space>
              <Button
                type="primary"
                icon={<FaCheck />}
                onClick={() => handleAprovar(record)}
                size="small"
              >
                Aprovar
              </Button>
              <Button
                danger
                icon={<FaTimes />}
                onClick={() => handleRejeitar(record)}
                size="small"
              >
                Rejeitar
              </Button>
            </Space>
          );
        }
        
        if (status === 1) {
          return (
            <Space>
              <Tag color="green">Aprovado</Tag>
              <Text type="secondary">
                {record.aprovacao ? dayjs(record.aprovacao).format('HH:mm') : '-'}
              </Text>
            </Space>
          );
        }
        
        return <Tag color="red">Rejeitado</Tag>;
      }
    }
  ];

  // ------------------------------
  // Componente para mostrar detalhes de uma pausa no calendário
  const PausaItem = ({ pausa }) => {
    const config = PAUSA_CONFIGS[pausa.descricao] || { color: '#722ed1', icon: <FaClock /> };
    
    return (
      <div
        style={{
          background: `${config.color}15`,
          borderLeft: `3px solid ${config.color}`,
          padding: '3px 6px',
          marginBottom: '3px',
          borderRadius: '3px',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: config.color, fontSize: '10px' }}>
            {config.icon}
          </span>
          <span style={{ fontWeight: 'bold' }}>
            {dayjs(pausa.aprovacao).format('HH:mm')}
          </span>
        </div>
        <div style={{ 
          maxWidth: '60px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            {pausa.nome.split(' ')[0]}
          </Text>
        </div>
      </div>
    );
  };

  // ------------------------------
  // Renderização das células do calendário com horas das pausas
  const dateCellRender = (value) => {
    const date = value.format("YYYY-MM-DD");
    const pausasDia = mapaPausas.filter(p => {
      if (!p.aprovacao) return false;
      return p.aprovacao.startsWith(date);
    });

    if (pausasDia.length === 0) return null;

    // Agrupar pausas por hora para melhor visualização
    const pausasPorHora = pausasDia.reduce((acc, pausa) => {
      const hora = dayjs(pausa.aprovacao).format('HH:mm');
      if (!acc[hora]) acc[hora] = [];
      acc[hora].push(pausa);
      return acc;
    }, {});

    // Ordenar horas
    const horasOrdenadas = Object.keys(pausasPorHora).sort();

    return (
      <Popover
        title={
          <Space>
            <FaCalendarDay />
            <Text strong>{value.format('DD/MM/YYYY')}</Text>
            <Badge count={pausasDia.length} style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        content={
          <div style={{ width: 250 }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {horasOrdenadas.map(hora => (
                <div key={hora}>
                  <div style={{ 
                    padding: '4px 8px', 
                    background: '#f0f0f0', 
                    borderRadius: '4px',
                    marginBottom: '4px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>
                    <FaClock style={{ marginRight: '6px' }} />
                    {hora}
                  </div>
                  {pausasPorHora[hora].map((pausa, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '6px',
                        marginBottom: '4px',
                        background: '#fafafa',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      <Space align="start">
                        <div style={{ 
                          color: PAUSA_CONFIGS[pausa.descricao]?.color || '#722ed1',
                          marginRight: '6px'
                        }}>
                          {PAUSA_CONFIGS[pausa.descricao]?.icon || <FaClock />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold' }}>{pausa.nome}</div>
                          <div style={{ color: '#666', fontSize: '11px' }}>
                            {pausa.descricao}
                          </div>
                        </div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          color: '#1890ff',
                          fontSize: '12px'
                        }}>
                          {dayjs(pausa.aprovacao).format('HH:mm')}
                        </div>
                      </Space>
                    </div>
                  ))}
                </div>
              ))}
            </Space>
          </div>
        }
        placement="right"
      >
        <div style={{ padding: '2px' }}>
          <Badge 
            count={pausasDia.length} 
            size="small" 
            style={{ 
              backgroundColor: '#52c41a',
              fontSize: '9px'
            }} 
          />
          
          {/* Mostrar apenas as 2 primeiras horas diferentes */}
          <div style={{ marginTop: '4px' }}>
            {horasOrdenadas.slice(0, 2).map(hora => (
              <div key={hora} style={{ marginBottom: '2px' }}>
                <div style={{ 
                  fontSize: '9px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  background: '#1890ff15',
                  padding: '1px 3px',
                  borderRadius: '2px',
                  display: 'inline-block'
                }}>
                  {hora}
                </div>
                <div style={{ fontSize: '8px', color: '#666', marginTop: '1px' }}>
                  {pausasPorHora[hora].length} pausa{pausasPorHora[hora].length !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
            
            {horasOrdenadas.length > 2 && (
              <div style={{ fontSize: '8px', color: '#666', textAlign: 'center' }}>
                +{horasOrdenadas.length - 2} horários
              </div>
            )}
          </div>
        </div>
      </Popover>
    );
  };

  // ------------------------------
  // Componente de legenda
 

  // ------------------------------
  // Estatísticas para o mês atual
  const getEstatisticasMes = () => {
    const mesAtual = currentDate.format('YYYY-MM');
    const pausasMes = mapaPausas.filter(p => 
      p.aprovacao && p.aprovacao.startsWith(mesAtual)
    );
    
    const totalPausas = pausasMes.length;
    const usuariosUnicos = [...new Set(pausasMes.map(p => p.nome))].length;
    
    // Contar pausas por período do dia
    const pausasManha = pausasMes.filter(p => {
      const hora = dayjs(p.aprovacao).hour();
      return hora >= 8 && hora < 12;
    }).length;
    
    const pausasTarde = pausasMes.filter(p => {
      const hora = dayjs(p.aprovacao).hour();
      return hora >= 12 && hora < 18;
    }).length;
    
    return { totalPausas, usuariosUnicos, pausasManha, pausasTarde };
  };

  const estatisticas = getEstatisticasMes();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ padding: 16 }}>
        <ToastContainer />

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <FaPause style={{ fontSize: 20, color: "#1677ff" }} />
                  <Title level={5} style={{ margin: 0 }}>
                    Gestão de Pausas - Supervisor
                  </Title>
                  {countPendentes > 0 && (
                    <Badge 
                      count={countPendentes} 
                      style={{ 
                        backgroundColor: '#ff4d4f',
                        marginLeft: 8
                      }} 
                    />
                  )}
                </Space>
              }
              extra={
                <Button
                  icon={<FaSync />}
                  onClick={() => {
                    fetchPausas();
                    fetchMapaPausas();
                  }}
                  size="small"
                >
                  Atualizar
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              {/* 🔥 TABELA COM AÇÕES */}
              <div style={{ marginBottom: 16 }}>
                <Space style={{ marginBottom: 8 }}>
                  <FaClock style={{ color: '#faad14' }} />
                  <Text strong>
                    Solicitações Pendentes: {countPendentes}
                  </Text>
                </Space>
                <Table
                  columns={columns}
                  dataSource={pausas}
                  rowKey="id"
                  loading={loading}
                  pagination={{ 
                    pageSize: 8,
                    size: 'small',
                    showSizeChanger: false
                  }}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={
                <Space>
                  <FaCalendarDay style={{ fontSize: 18, color: "#52c41a" }} />
                  <Text strong>Mapa de Pausas Aprovadas</Text>
                  <Badge 
                    count={mapaPausas.length} 
                    style={{ backgroundColor: '#52c41a' }} 
                  />
                </Space>
              }
              extra={
                <Space>
                  <Text type="secondary">
                    {currentDate.format('MMMM [de] YYYY')}
                  </Text>
                </Space>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Calendar
                    value={currentDate}
                    onChange={setCurrentDate}
                    fullscreen={false}
                    dateCellRender={dateCellRender}
                    headerRender={() => null}
                    style={{ 
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: 8
                    }}
                  />
                </Col>
                
                {/* Estatísticas do Mês */}
                <Col span={24}>
                  <Card size="small">
                    <Row gutter={[16, 8]}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                            {estatisticas.totalPausas}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>Total de Pausas</Text>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                            {estatisticas.usuariosUnicos}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>Usuários</Text>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#faad14' }}>
                            {estatisticas.pausasManha}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>Manhã (8h-12h)</Text>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                            {estatisticas.pausasTarde}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>Tarde (12h-18h)</Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                
               
              </Row>
              
               
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default GestaoPausas;